import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Light or dark for the everyday screens.
//
// "Light everyday, dark for play, and the reveal always blooms into light" is a
// narrative rule, not a preference: the drop is anticipation and the reveal is
// the moment the truth arrives. So this only controls the everyday. Play and
// reveal force their own surface with useSurface() and ignore what is set here.

export type ThemeChoice = "light" | "dark" | "system";

const KEY = "theme_choice";

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* private mode */ }
  return "light"; // the everyday starts light
}

interface ThemeState {
  choice: ThemeChoice;
  setChoice: (c: ThemeChoice) => void;
}

const Ctx = createContext<ThemeState>({ choice: "light", setChoice: () => {} });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, set] = useState<ThemeChoice>(read);

  useEffect(() => {
    const root = document.documentElement;
    // "system" removes the attribute entirely so the prefers-color-scheme block
    // in the stylesheet is what decides. Writing data-theme="system" would match
    // neither rule and silently strand the page on the light palette.
    if (choice === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", choice);
  }, [choice]);

  return (
    <Ctx.Provider
      value={{
        choice,
        setChoice: (c) => {
          set(c);
          try { localStorage.setItem(KEY, c); } catch { /* private mode */ }
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useTheme = () => useContext(Ctx);

// Force a surface for one screen, whatever the person's preference is.
// Returns the prop to spread on the screen's outermost element.
export function useSurface(surface: "light" | "dark") {
  return { "data-surface": surface } as const;
}
